import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  TicketIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  BugAntIcon,
  LightBulbIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

interface SupportCategory {
  id: 'technical' | 'billing' | 'feature-request' | 'bug-report' | 'general';
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  color: string;
}

const SupportPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: '' as SupportCategory['id'] | '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');

  const categories: SupportCategory[] = [
    {
      id: 'technical',
      name: 'Technical Issue',
      icon: ExclamationTriangleIcon,
      description: 'Login problems, website errors, or app crashes',
      color: 'text-red-600 bg-red-100'
    },
    {
      id: 'billing',
      name: 'Billing & Payments',
      icon: CreditCardIcon,
      description: 'Subscription issues, payment problems, or billing questions',
      color: 'text-blue-600 bg-blue-100'
    },
    {
      id: 'feature-request',
      name: 'Feature Request',
      icon: LightBulbIcon,
      description: 'Suggest new features or improvements',
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      id: 'bug-report',
      name: 'Bug Report',
      icon: BugAntIcon,
      description: 'Report unexpected behavior or issues',
      color: 'text-purple-600 bg-purple-100'
    },
    {
      id: 'general',
      name: 'General Question',
      icon: QuestionMarkCircleIcon,
      description: 'General questions about using Workazon',
      color: 'text-green-600 bg-green-100'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim() || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.post('/support/tickets', {
        subject: formData.subject,
        description: formData.description,
        category: formData.category,
        priority: formData.priority
      });

      if (response.data.success) {
        setTicketNumber(response.data.data.ticketNumber);
        setSubmitted(true);
        toast.success('Support ticket submitted successfully!');
      }
    } catch (error: any) {
      console.error('Submit ticket error:', error);
      toast.error('Failed to submit support ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: SupportCategory['id']) => {
    setFormData({ ...formData, category: categoryId });
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      description: '',
      category: '',
      priority: 'medium'
    });
    setSubmitted(false);
    setTicketNumber('');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <TicketIcon className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Support Ticket Submitted Successfully!
            </h1>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 font-medium">
                Ticket Number: <span className="font-mono">{ticketNumber}</span>
              </p>
            </div>
            
            <div className="text-left bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-medium text-gray-900 mb-3">What happens next?</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">✓</span>
                  <span className="ml-2">We've received your support request</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">✓</span>
                  <span className="ml-2">A confirmation email has been sent to your email address</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-blue-500 mt-0.5">⏳</span>
                  <span className="ml-2">Our support team will review your request within 24 hours</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-blue-500 mt-0.5">📧</span>
                  <span className="ml-2">You'll receive updates via email as we work on your request</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={resetForm}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Submit Another Ticket
              </button>
              
              <button
                onClick={() => window.history.back()}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Get Help & Support</h1>
          <p className="text-lg text-gray-600">
            We're here to help! Submit a support ticket and our team will get back to you soon.
          </p>
        </div>

        {/* Quick Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <InformationCircleIcon className="flex-shrink-0 h-6 w-6 text-blue-600 mt-1" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-900">
                Need quick answers?
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Check our{' '}
                  <a href="/dashboard" className="font-medium underline hover:text-blue-600">
                    Help Center
                  </a>{' '}
                  for instant answers to common questions, or browse our{' '}
                  <a href="/support" className="font-medium underline hover:text-blue-600">
                    FAQ section
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="px-6 py-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Category Selection */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  What can we help you with? *
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleCategorySelect(category.id)}
                        className={`relative p-4 rounded-lg border-2 text-left transition-all hover:shadow-sm ${
                          formData.category === category.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start">
                          <div className={`flex-shrink-0 p-2 rounded-lg ${category.color}`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div className="ml-3 flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900">
                              {category.name}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {category.description}
                            </p>
                          </div>
                        </div>
                        {formData.category === category.id && (
                          <div className="absolute top-2 right-2">
                            <div className="h-5 w-5 bg-indigo-500 rounded-full flex items-center justify-center">
                              <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Priority Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Priority Level
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'low', label: 'Low', description: 'General question', color: 'text-green-600' },
                    { value: 'medium', label: 'Medium', description: 'Standard issue', color: 'text-yellow-600' },
                    { value: 'high', label: 'High', description: 'Important problem', color: 'text-orange-600' },
                    { value: 'urgent', label: 'Urgent', description: 'Critical issue', color: 'text-red-600' }
                  ].map((priority) => (
                    <label key={priority.value} className="relative flex items-center">
                      <input
                        type="radio"
                        name="priority"
                        value={priority.value}
                        checked={formData.priority === priority.value}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                        className="sr-only"
                      />
                      <div className={`flex-1 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.priority === priority.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="text-center">
                          <div className={`text-sm font-medium ${priority.color}`}>
                            {priority.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {priority.description}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Brief description of your issue..."
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  id="description"
                  rows={6}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Please provide as much detail as possible about your issue. Include steps to reproduce the problem, error messages, or any other relevant information..."
                  required
                />
                <p className="mt-2 text-sm text-gray-500">
                  The more details you provide, the faster we can help resolve your issue.
                </p>
              </div>

              {/* User Info Display */}
              {currentUser && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Contact Information</h4>
                  <div className="text-sm text-gray-600">
                    <p><strong>Name:</strong> {userProfile ? `${userProfile.firstName} ${userProfile.lastName}`.trim() : (currentUser.displayName || 'Not provided')}</p>
                    <p><strong>Email:</strong> {currentUser.email}</p>
                    <p><strong>Account Type:</strong> {userProfile?.userType || 'Not specified'}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.subject.trim() || !formData.description.trim() || !formData.category}
                  className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <TicketIcon className="h-4 w-4 mr-2" />
                      Submit Support Ticket
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Need immediate assistance?{' '}
            <a
              href="mailto:support@workazon.com"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Email us directly
            </a>{' '}
            or check our{' '}
            <a
              href="/dashboard"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Help Center
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;